import classNames from 'classnames';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import FlightSelectorFlightCardSection from './flight-selector-card-section/flight-selector-card-section';
import styles from './flight-selector-card.module.scss';

type FlightSelectorTransport = Content.Components['FlightSelectorTransportDto'];

interface FlightSelectorCardProps extends FlightSelectorTransport {
  /** The class name to apply */
  className?: string;
  /** Indicates if the slide is active */
  isActive?: boolean;
  /** Indicates if the price is relative */
  isPriceRelative?: boolean;
  /** The tooltip content to show when there is no time */
  noTimeHeadline: string;
  /** The text to show when there is no time */
  noTimeText: string;
  /** An event that is triggered on click */
  onClick: () => void;
}

const FlightSelectorCard = ({
  className,
  homeboundFlightInformation,
  infoWarningText,
  isActive,
  infoWarningLink,
  isPriceRelative,
  noTimeHeadline,
  noTimeText,
  onClick,
  outboundFlightInformation,
  totalPrice,
}: FlightSelectorCardProps) => {
  const formattedPrice = useCurrencyFormatter()(totalPrice);

  const priceRelativeSymbol = isPriceRelative && totalPrice >= 0 ? '+' : '';

  const renderContent = (
    <div className={styles.content}>
      <FlightSelectorFlightCardSection
        {...outboundFlightInformation}
        noTimeHeadline={noTimeHeadline}
        noTimeText={noTimeText}
      />

      <div className={styles.divider} />

      <FlightSelectorFlightCardSection
        {...homeboundFlightInformation}
        noTimeHeadline={noTimeHeadline}
        noTimeText={noTimeText}
      />

      {!isActive && (
        <Typography className={styles.price} tagStyle="priceSmall">
          {`${priceRelativeSymbol}${formattedPrice}`}
        </Typography>
      )}
    </div>
  );

  if (isActive) {
    return (
      <div className={classNames(styles.component, className, styles.isActive)}>
        {renderContent}

        {infoWarningText && (
          <div className={styles.infoWarning}>
            <Icon className={styles.infoWarningIcon} name="Warning" />

            {infoWarningLink ? (
              <RichText
                className={styles.infoWarningText}
                text={`${infoWarningText} <a href="${infoWarningLink.url}" target="${infoWarningLink.target}">${infoWarningLink.text}</a>`}
              />
            ) : (
              <RichText
                className={styles.infoWarningText}
                text={`${infoWarningText}`}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      className={classNames(styles.component, className)}
      onClick={onClick}
    >
      {renderContent}
    </button>
  );
};

FlightSelectorCard.displayName = 'FlightSelectorCard';

export default FlightSelectorCard;
